import { View, Text, FlatList, Pressable, StyleSheet, ActivityIndicator, RefreshControl } from 'react-native'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useDataLoading } from '../../../src/hooks/useDataLoading'
import { useSafeAPI } from '../../../src/hooks/useSafeAPI'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import ContentContainer from '../../../src/components/ContentContainer'
export { ScreenErrorBoundary as ErrorBoundary } from '../../../src/components/ScreenErrorBoundary'
import { ArrowLeft, Zap, Smartphone, Wifi, FileText, Info, Check } from 'lucide-react-native'
import { apiClient } from '../../../src/api/client'
import { useAuth } from '../../../src/auth/AuthProvider'
import AcceptQuoteModal, { type AcceptableQuote } from '../../../src/components/AcceptQuoteModal'
import SharedQuoteCard, { type QuoteData, type Badge, assignBadges } from '../../../src/components/QuoteCard'
import { calcEnergyTotal, type TariffData, DEFAULT_KWH_YEAR } from '../../../src/components/EnergyBreakdown'
import { computeEffectivePrice, computeQuoteSavings } from '../../../src/utils/savings'
import { appEvents } from '../../../src/lib/events'
import { colors, spacing, borderRadius, typography } from '../../../src/theme'
import { storage, USER_ID_KEY } from '../../../src/utils/storage'
import { toast } from '../../../src/components/Toast'
import { haptics } from '../../../src/utils/haptics'
import { sentryCaptureException } from '../../../src/lib/sentry'

interface PublicPlan {
  provider: string
  product_name?: string
  monthly_price?: number
  monthly_subscription?: number
  kwh_surcharge?: number
  estimated_monthly_total?: number
  energy_breakdown?: Record<string, number>
  data_dk_gb?: number | null  // null = unlimited
  data_eu_gb?: number
  download_mbit?: number
  upload_mbit?: number
  eu_data?: boolean
  network?: string
  speed?: string
  technology?: string
  green_energy?: boolean
  is_spot_based?: boolean
  contract_length?: string | number
  price_type?: string
  payment_type?: string
  creation_fee_kr?: number
  cancellation_fee_kr?: number
  calls_sms?: string
  hotspot_tethering?: string
  roaming_terms?: string
  special_features?: string[]
  router_included?: boolean
  router_model?: string
  cancellation_notice?: string
  coverage_area?: string
  creation_fee?: number
  intro_offer?: any
  source: string
}

interface MarketQuote {
  id: string
  provider: string
  product_name?: string
  category: string
  monthly_price: number
  details: Record<string, any>
  estimated_annual_savings?: number
}

interface CurrentAgreement {
  provider: string
  price: number
  priceConfirmed: boolean
  details?: string
  yearlyKwh?: number
}

// Convert filter object to string-keyed params for API (skip undefined/null)
function _buildFilters(catFilters: Record<string, any>, base: Record<string, string> = {}): Record<string, string> {
  const out: Record<string, string> = { ...base }
  for (const [k, v] of Object.entries(catFilters)) {
    if (v !== undefined && v !== null) out[k] = String(v)
  }
  return out
}

const CATEGORIES = [
  { key: 'mobile', label: 'Mobil', icon: Smartphone, color: colors.categories.mobile, bg: colors.categoriesBg.mobile },
  { key: 'internet', label: 'Internet', icon: Wifi, color: colors.categories.internet, bg: colors.categoriesBg.internet },
  { key: 'energy', label: 'Strøm', icon: Zap, color: colors.categories.energy, bg: colors.categoriesBg.energy },
]


// ─── planToQuote ─────────────────────────────────

function planToQuote(plan: PublicPlan, category: string, idx: number, tariffs?: TariffData | null, yearlyKwh?: number): MarketQuote {
  let price: number
  const details: Record<string, any> = {}

  // Energy: recalculate from tariffs to match EnergyBreakdownAccordion numbers
  if (category === 'energy' && tariffs) {
    const kwh = yearlyKwh || DEFAULT_KWH_YEAR
    const monthlyKwh = kwh / 12
    const sub = Number(plan.monthly_subscription) || 0
    const surchargeOre = Number(plan.kwh_surcharge) || 0
    const kwhCost = (surchargeOre / 100) * monthlyKwh
    const spotCost = (tariffs.spot_price_ore / 100) * monthlyKwh
    const nettarifCost = (tariffs.nettarif_ore / 100) * monthlyKwh
    const nettarifAbo = tariffs.nettarif_detail.abonnement_kr_md
    const elafgiftCost = (tariffs.elafgift_ore / 100) * monthlyKwh
    const systemtarifCost = (tariffs.systemtarif_ore / 100) * monthlyKwh
    const transmissionstarifCost = (tariffs.transmissionstarif_ore / 100) * monthlyKwh

    const breakdown: Record<string, number> = {
      subscription: sub,
      kwh_cost: kwhCost,
      spot_price: spotCost,
      nettarif: nettarifCost,
      nettarif_abonnement: nettarifAbo,
      elafgift: elafgiftCost,
      systemtarif: systemtarifCost,
      transmissionstarif: transmissionstarifCost,
    }
    // Use calcEnergyTotal for consistent rounding with breakdown display
    price = calcEnergyTotal(breakdown, tariffs as TariffData, kwh)
    details.energy_breakdown = breakdown
    details.yearly_kwh = kwh
    if (plan.kwh_surcharge != null) details.kwh_surcharge = plan.kwh_surcharge
    if (plan.monthly_subscription != null) details.monthly_subscription = plan.monthly_subscription
  } else {
    price = plan.estimated_monthly_total ?? plan.monthly_price ?? plan.monthly_subscription ?? 0
    if (plan.kwh_surcharge != null) details.kwh_surcharge = plan.kwh_surcharge
    if (plan.monthly_subscription != null) details.monthly_subscription = plan.monthly_subscription
    if (plan.energy_breakdown) details.energy_breakdown = plan.energy_breakdown
  }

  if (plan.green_energy) details.green_energy = true
  if (plan.is_spot_based) details.is_spot_based = true
  if (plan.data_dk_gb !== undefined) details.gb_dk = plan.data_dk_gb ?? null
  if (plan.data_eu_gb) details.data_eu_gb = plan.data_eu_gb
  if (plan.eu_data) details.eu_data = true
  if (plan.network) details.network = plan.network
  if (plan.speed) details.speed = plan.speed
  if (plan.calls_sms) details.calls_sms = plan.calls_sms
  if (plan.hotspot_tethering) details.hotspot_tethering = plan.hotspot_tethering
  if (plan.roaming_terms) details.roaming_terms = plan.roaming_terms
  if (plan.special_features) details.special_features = plan.special_features
  if (plan.download_mbit) details.download_mbit = plan.download_mbit
  if (plan.upload_mbit) details.upload_mbit = plan.upload_mbit
  if (plan.technology) details.technology = plan.technology
  if (plan.router_included != null) details.router_included = plan.router_included
  if (plan.router_model) details.router_model = plan.router_model
  if (plan.cancellation_notice) details.cancellation_notice = plan.cancellation_notice
  if (plan.coverage_area) details.coverage_area = plan.coverage_area
  if (plan.creation_fee != null) details.creation_fee = plan.creation_fee
  if (plan.contract_length != null) {
    const len = plan.contract_length
    if (len === 0 || len === 'Ingen binding') details.contract_length = 'Ingen binding'
    else if (typeof len === 'number') details.contract_length = `${len} md.`
    else details.contract_length = String(len)
  }
  if (plan.price_type) details.price_type = plan.price_type
  if (plan.payment_type) details.payment_type = plan.payment_type
  if (plan.creation_fee_kr) details.creation_fee_kr = plan.creation_fee_kr
  if (plan.cancellation_fee_kr) details.cancellation_fee_kr = plan.cancellation_fee_kr
  if (plan.intro_offer) details.intro_offer = plan.intro_offer
  details.source = plan.source

  return {
    id: `market-${category}-${idx}`,
    provider: plan.provider,
    product_name: plan.product_name,
    category,
    monthly_price: price,
    details,
  }
}

// Derive initial filter state from user's onboarding data
function _deriveFilters(activeReq: any): {
  mobile: { min_gb?: number; eu_data?: boolean; world_data?: boolean }
  internet: { min_speed?: number }
  energy: {}
} {
  const ud = activeReq?.user_data || {}
  const mobileData = Array.isArray(ud.mobile) ? ud.mobile[0] : ud.mobile
  const internetData = Array.isArray(ud.internet) ? ud.internet[0] : ud.internet

  const mobileF: { min_gb?: number; eu_data?: boolean; world_data?: boolean } = {}
  const gb = Number(mobileData?.desired_gb_dk)
  if (gb > 0) mobileF.min_gb = gb
  if (mobileData?.eu_data === true || mobileData?.eu_data === 'true') mobileF.eu_data = true

  const internetF: { min_speed?: number } = {}
  if (internetData?.needs === 'premium') internetF.min_speed = 300
  else if (internetData?.needs === 'standard') internetF.min_speed = 100

  return { mobile: mobileF, internet: internetF, energy: {} }
}

export default function MarketPage() {
  const router = useRouter()
  const { category: paramCategory } = useLocalSearchParams<{ category?: string }>()
  const { user } = useAuth()

  const [activeCategory, setActiveCategory] = useState(paramCategory || 'mobile')
  const [plans, setPlans] = useState<Record<string, MarketQuote[]>>({})
  const { loading, refreshing, loadError, setLoading, setRefreshing, setLoadError } = useDataLoading()
  const { safe, hadError, reset: resetSafeError } = useSafeAPI('market')
  const [uniqueOnly, setUniqueOnly] = useState(false)
  const [currentAgreements, setCurrentAgreements] = useState<Record<string, CurrentAgreement>>({})
  const [tariffs, setTariffs] = useState<TariffData | null>(null)
  const [quoteRequests, setQuoteRequests] = useState<any[]>([])
  const [personalQuotes, setPersonalQuotes] = useState<MarketQuote[]>([])
  const [categoryLoading, setCategoryLoading] = useState<Record<string, boolean>>({})
  const [categoryFilters, setCategoryFilters] = useState<{
    mobile: { min_gb?: number; eu_data?: boolean; world_data?: boolean }
    internet: { min_speed?: number }
    energy: { green_energy?: boolean; prepaid?: boolean }
  }>({ mobile: {}, internet: {}, energy: {} })

  // Stale request protection
  const loadIdRef = useRef(0)
  const defaultCategorySetRef = useRef(false)

  useEffect(() => {
    const id = ++loadIdRef.current
    loadData(id)
  }, [uniqueOnly])

  const loadData = async (loadId?: number) => {
    setLoading(true)
    setLoadError(false)
    try {
      resetSafeError()

      // Phase 1: fetch user data — needed to derive correct filters before hitting price APIs
      const [requestsRes, userRes, billHistoryRes, quotesRes] = await Promise.all([
        user?.id ? safe(apiClient.quotes.getRequests(user.id), { data: [] } as any) : Promise.resolve({ data: [] }),
        user?.id ? safe(apiClient.users.getMe(user.id), { data: null } as any) : Promise.resolve({ data: null }),
        user?.id ? safe(apiClient.contracts.billHistory(), { data: { averages: {} } } as any) : Promise.resolve({ data: { averages: {} } }),
        user?.id ? safe(apiClient.quotes.list(user.id), { data: [] } as any) : Promise.resolve({ data: [] }),
      ])
      if (loadId !== undefined && loadId !== loadIdRef.current) return

      if (userRes.data) {
        setUserProfile({ address: userRes.data.address, phone: userRes.data.phone, full_name: userRes.data.full_name })
        setHasPaymentInfo(userRes.data.has_payment_info === true)
        setExistingPaymentReg(userRes.data.payment_reg_masked || null)
        setExistingPaymentAccount(userRes.data.payment_account_masked || null)
      }

      // Extract current agreements from quote requests
      const reqs = requestsRes.data || []
      setQuoteRequests(reqs)
      const active = reqs.find((r: any) => ['pending', 'pending_approval', 'sent'].includes(r.status))
      const agreements: Record<string, CurrentAgreement> = {}
      if (active?.user_data) {
        for (const [cat, data] of Object.entries(active.user_data as Record<string, any>)) {
          const planList = Array.isArray(data) ? data : [data]
          const p = planList[0]
          if (!p || typeof p !== 'object') continue
          const provider = p.provider || ''
          const billAvgs = billHistoryRes.data?.averages || {}
          const billPrice = billAvgs[cat]?.confirmed_current_price?.monthly_price
          const confirmedPrice = p.confirmed_price ? Number(p.confirmed_price) : undefined
          const price = billPrice ?? confirmedPrice ?? (Number(p.price) || 0)
          const priceConfirmed = billPrice != null || !!confirmedPrice
          const yearlyKwhVal = cat === 'energy' ? (Number(p.yearly_kwh) || undefined) : undefined
          const details = cat === 'energy' && yearlyKwhVal ? `${yearlyKwhVal.toLocaleString('da-DK')} kWh/år` : ''
          if (provider && price > 0) {
            agreements[cat] = { provider, price, priceConfirmed, details, yearlyKwh: yearlyKwhVal }
          }
        }
      }
      setCurrentAgreements(agreements)

      // Derive intelligent initial filters from user's onboarding data
      const derivedFilters = _deriveFilters(active)
      const baseFilters = uniqueOnly ? { unique_per_provider: 'true' } : {}

      // Use manual filters if already set (e.g. pull-to-refresh), otherwise use derived
      const hasManualFilters = Object.values(categoryFilters).some(f => Object.keys(f).length > 0)
      const filtersToUse = hasManualFilters ? categoryFilters : derivedFilters
      const hasDerived = (Object.values(derivedFilters) as Record<string, any>[]).some(f => Object.keys(f).length > 0)
      if (!hasManualFilters && hasDerived) setCategoryFilters(derivedFilters)

      // Smart default active category: first category with an active quote request (only on initial load)
      if (!paramCategory && !defaultCategorySetRef.current) {
        const catWithRequest = ['mobile', 'energy', 'internet'].find(cat => active?.user_data?.[cat])
        if (catWithRequest) {
          setActiveCategory(catWithRequest)
          defaultCategorySetRef.current = true
        }
      }

      // Phase 2: fetch prices once with correct filters
      const [energyRes, mobileRes, internetRes] = await Promise.all([
        safe(apiClient.publicPrices.cheapest('energy', 100, _buildFilters(filtersToUse.energy, baseFilters)), { data: { plans: [], tariffs: null } } as any),
        safe(apiClient.publicPrices.cheapest('mobile', 100, _buildFilters(filtersToUse.mobile, baseFilters)), { data: [] } as any),
        safe(apiClient.publicPrices.cheapest('internet', 100, _buildFilters(filtersToUse.internet, baseFilters)), { data: [] } as any),
      ])
      if (loadId !== undefined && loadId !== loadIdRef.current) return
      if (hadError.current) setLoadError(true)

      const result: Record<string, MarketQuote[]> = {}
      const energyData = energyRes.data
      const energyTariffs = energyData?.tariffs || null
      if (energyTariffs) setTariffs(energyTariffs)

      // Convert personalized quotes to MarketQuote format
      const freshPersonalQuotes: MarketQuote[] = ((quotesRes.data || []) as any[])
        .filter((q: any) => q.status !== 'superseded' && q.status !== 'expired' && q.status !== 'accepted')
        .map((q: any) => ({
          id: q.id,
          provider: q.provider,
          product_name: q.product_name,
          category: q.category,
          monthly_price: Number(q.monthly_price),
          details: { ...q.details, is_personalized: true },
          estimated_annual_savings: q.estimated_annual_savings ? Number(q.estimated_annual_savings) : undefined,
        }))
      setPersonalQuotes(freshPersonalQuotes)

      // Helper: merge personalized first, then public (dedup by provider)
      const mergeWithPersonal = (publicList: MarketQuote[], cat: string): MarketQuote[] => {
        const personal = freshPersonalQuotes.filter(q => q.category === cat)
        const personalProviders = new Set(personal.map(q => q.provider.toLowerCase().trim()))
        const dedupedPublic = publicList.filter(q => !personalProviders.has(q.provider.toLowerCase().trim()))
        return [...personal, ...dedupedPublic]
      }

      if (energyData?.plans?.length) {
        const kwh = agreements.energy?.yearlyKwh
        const publicEnergy = energyData.plans
          .map((p: PublicPlan, i: number) => planToQuote(p, 'energy', i, energyTariffs, kwh))
          .filter((q: MarketQuote) => q.monthly_price > 0)
        result.energy = mergeWithPersonal(publicEnergy, 'energy')
      } else if (freshPersonalQuotes.some(q => q.category === 'energy')) {
        result.energy = freshPersonalQuotes.filter(q => q.category === 'energy')
      }
      const mobileData = mobileRes.data
      if (Array.isArray(mobileData) && mobileData.length) {
        const publicMobile = mobileData
          .map((p: PublicPlan, i: number) => planToQuote(p, 'mobile', i))
          .filter((q: MarketQuote) => q.monthly_price > 0)
        result.mobile = mergeWithPersonal(publicMobile, 'mobile')
      } else if (freshPersonalQuotes.some(q => q.category === 'mobile')) {
        result.mobile = freshPersonalQuotes.filter(q => q.category === 'mobile')
      }
      const internetData = internetRes.data
      if (Array.isArray(internetData) && internetData.length) {
        const publicInternet = internetData
          .map((p: PublicPlan, i: number) => planToQuote(p, 'internet', i))
          .filter((q: MarketQuote) => q.monthly_price > 0)
        result.internet = mergeWithPersonal(publicInternet, 'internet')
      } else if (freshPersonalQuotes.some(q => q.category === 'internet')) {
        result.internet = freshPersonalQuotes.filter(q => q.category === 'internet')
      }
      setPlans(result)
    } catch (err: any) {
      sentryCaptureException(err, { screen: 'market' })
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }

  const reloadCategoryWith = async (cat: string, params: Record<string, string>) => {
    setCategoryLoading(prev => ({ ...prev, [cat]: true }))
    try {
      let publicPlans: MarketQuote[] = []
      if (cat === 'energy') {
        const res = await apiClient.publicPrices.cheapest('energy', 100, params)
        const energyData = res.data
        const energyTariffs = energyData?.tariffs || null
        if (energyTariffs) setTariffs(energyTariffs)
        const kwh = currentAgreements.energy?.yearlyKwh
        publicPlans = (energyData?.plans || [])
          .map((p: PublicPlan, i: number) => planToQuote(p, 'energy', i, energyTariffs, kwh))
          .filter((q: MarketQuote) => q.monthly_price > 0)
      } else {
        const res = await apiClient.publicPrices.cheapest(cat, 100, params)
        publicPlans = (Array.isArray(res.data) ? res.data : [])
          .map((p: PublicPlan, i: number) => planToQuote(p, cat, i))
          .filter((q: MarketQuote) => q.monthly_price > 0)
      }
      const personal = personalQuotes.filter(q => q.category === cat)
      const personalProviders = new Set(personal.map(q => q.provider.toLowerCase().trim()))
      const merged = [...personal, ...publicPlans.filter(q => !personalProviders.has(q.provider.toLowerCase().trim()))]
      setPlans(prev => ({ ...prev, [cat]: merged }))
    } catch (err: any) {
      sentryCaptureException(err, { screen: 'market', action: 'reload_category' })
    } finally {
      setCategoryLoading(prev => ({ ...prev, [cat]: false }))
    }
  }

  const [acceptModalQuote, setAcceptModalQuote] = useState<AcceptableQuote | null>(null)
  const latchedAcceptQuoteRef = useRef<AcceptableQuote | null>(null)
  if (acceptModalQuote) latchedAcceptQuoteRef.current = acceptModalQuote
  const latchedAcceptQuote = latchedAcceptQuoteRef.current
  const [userProfile, setUserProfile] = useState<{ address?: string; phone?: string; full_name?: string } | null>(null)
  const [hasPaymentInfo, setHasPaymentInfo] = useState(false)
  const [existingPaymentReg, setExistingPaymentReg] = useState<string | null>(null)
  const [existingPaymentAccount, setExistingPaymentAccount] = useState<string | null>(null)

  const currentPlans = plans[activeCategory] || []
  const currentAgreement = currentAgreements[activeCategory]

  // Smart badge assignment using shared logic (consistent with Tilbud page)
  const badgeMap = useMemo(() => {
    if (currentPlans.length === 0) return new Map<string, Badge>()
    const top4 = currentPlans.slice(0, 4)
    return assignBadges(top4 as QuoteData[], activeCategory)
  }, [currentPlans, activeCategory])

  // Savings — uses shared computeEffectivePrice/computeQuoteSavings (same formula as Dashboard + Tilbud)
  const bestSavings = useMemo(() => {
    if (!currentAgreement || currentAgreement.price <= 0 || currentPlans.length === 0) return null
    const yearlyKwh = currentAgreement?.yearlyKwh
    const cheapest = Math.min(...currentPlans.map(q =>
      computeEffectivePrice(q as any, tariffs, yearlyKwh)
    ))
    const best = currentAgreement.price - cheapest
    return best > 0 ? Math.round(best * 12) : null
  }, [currentAgreement, currentPlans, tariffs])

  const getSavingsAnnual = (quote: MarketQuote): number | undefined => {
    if (!currentAgreement || currentAgreement.price <= 0) return undefined
    return computeQuoteSavings(currentAgreement.price, quote as any, tariffs, currentAgreement?.yearlyKwh)
  }

  // Pre-compute modal props outside JSX to avoid IIFE pattern (causes "Unexpected text node" on RN Web)
  const modalOnboardingPlans = useMemo(() => {
    if (!acceptModalQuote) return undefined
    const activeReq = quoteRequests.find((r: any) => ['pending', 'pending_approval', 'sent'].includes(r.status))
    const catPlans = activeReq?.user_data?.[acceptModalQuote.category]
    return Array.isArray(catPlans) ? catPlans : undefined
  }, [acceptModalQuote, quoteRequests])

  return (
    <SafeAreaView style={styles.container} edges={[]}>
      <ContentContainer>
      {/* Header */}
      <View style={styles.header}>
        <Pressable onPress={() => router.back()} style={styles.backButton}>
          <ArrowLeft size={20} color={colors.text.primary} />
        </Pressable>
        <View style={{ flex: 1 }}>
          <View style={styles.headerTitleRow}>
            <Text style={styles.headerTitle}>Markedsoverblik</Text>
            {bestSavings && (
              <View style={styles.savingsBadge}>
                <Text style={styles.savingsBadgeText}>
                  Spar op til {bestSavings.toLocaleString('da-DK')} kr/år
                </Text>
              </View>
            )}
          </View>
          <Text style={styles.headerSubtitle}>Alle tilgængelige planer sorteret efter pris</Text>
        </View>
      </View>

      {loadError && (
        <View style={styles.errorBanner}>
          <Info size={16} color={colors.warningDark} />
          <Text style={styles.errorText}>Noget gik galt — vores team er notificeret.</Text>
          <Pressable onPress={() => loadData()}>
            <Text style={styles.errorRetry}>Prøv igen</Text>
          </Pressable>
        </View>
      )}

      {/* Current agreement */}
      {currentAgreement && (
        <View style={styles.currentAgreement}>
          <View style={styles.currentAgreementAvatar}>
            <Text style={styles.currentAgreementInitial}>{currentAgreement.provider.charAt(0)}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.currentAgreementLabel}>
              Din nuværende: <Text style={{ fontWeight: '600', color: colors.text.primary }}>{currentAgreement.provider}</Text>
            </Text>
            {currentAgreement.details && <Text style={styles.currentAgreementDetails}>{currentAgreement.details}</Text>}
          </View>
          <Text style={styles.currentAgreementPrice}>
            {currentAgreement.price.toLocaleString('da-DK')} <Text style={styles.currentAgreementUnit}>kr/md</Text>
          </Text>
        </View>
      )}

      {/* Category tabs */}
      <View style={styles.categoryTabs}>
        {CATEGORIES.map(cat => {
          const Icon = cat.icon
          const count = plans[cat.key]?.length || 0
          const isActive = activeCategory === cat.key
          return (
            <Pressable
              key={cat.key}
              style={[styles.categoryTab, isActive && styles.categoryTabActive]}
              onPress={() => setActiveCategory(cat.key)}
            >
              <Icon size={16} color={isActive ? cat.color : colors.text.muted} />
              <Text style={[styles.categoryTabLabel, isActive && { color: colors.text.primary, fontWeight: '600' }]}>
                {cat.label}
              </Text>
              {count > 0 && (
                <Text style={[styles.categoryTabCount, isActive && { color: colors.text.secondary }]}>
                  ({count})
                </Text>
              )}
            </Pressable>
          )
        })}
      </View>

      {/* Filter row: unique per provider + plan count */}
      <View style={styles.filterRow}>
        <Pressable
          style={[styles.filterChip, uniqueOnly && styles.filterChipActive]}
          onPress={() => { haptics.selection(); setUniqueOnly(!uniqueOnly) }}
        >
          {uniqueOnly && <Check size={12} color={colors.text.inverse} />}
          <Text style={[styles.filterChipText, uniqueOnly && styles.filterChipTextActive]}>
            1 plan per udbyder
          </Text>
        </Pressable>
        <Text style={styles.planCount}>
          {categoryLoading[activeCategory] ? '...' : `${currentPlans.length} ${currentPlans.length === 1 ? 'plan' : 'planer'}`}
        </Text>
      </View>

      {/* Category-specific filter chips */}
      {activeCategory === 'mobile' && (
        <View style={styles.categoryFilterRow}>
          {([undefined, 10, 20, 50] as (number | undefined)[]).map(gb => {
            const active = (categoryFilters.mobile.min_gb ?? undefined) === gb
            const label = gb === undefined ? 'Alle GB' : `${gb}+ GB`
            return (
              <Pressable
                key={String(gb)}
                style={[styles.filterChip, active && styles.filterChipActive]}
                onPress={() => {
                  haptics.selection()
                  const newF = { ...categoryFilters.mobile, min_gb: gb }
                  setCategoryFilters(f => ({ ...f, mobile: newF }))
                  reloadCategoryWith('mobile', _buildFilters(newF, uniqueOnly ? { unique_per_provider: 'true' } : {}))
                }}
              >
                <Text style={[styles.filterChipText, active && styles.filterChipTextActive]}>{label}</Text>
              </Pressable>
            )
          })}
          <Pressable
            style={[styles.filterChip, categoryFilters.mobile.eu_data === true && styles.filterChipActive]}
            onPress={() => {
              haptics.selection()
              const newF = { ...categoryFilters.mobile, eu_data: categoryFilters.mobile.eu_data === true ? undefined : true }
              setCategoryFilters(f => ({ ...f, mobile: newF }))
              reloadCategoryWith('mobile', _buildFilters(newF, uniqueOnly ? { unique_per_provider: 'true' } : {}))
            }}
          >
            <Text style={[styles.filterChipText, categoryFilters.mobile.eu_data === true && styles.filterChipTextActive]}>EU data</Text>
          </Pressable>
          <Pressable
            style={[styles.filterChip, categoryFilters.mobile.world_data === true && styles.filterChipActive]}
            onPress={() => {
              haptics.selection()
              const newF = { ...categoryFilters.mobile, world_data: categoryFilters.mobile.world_data === true ? undefined : true }
              setCategoryFilters(f => ({ ...f, mobile: newF }))
              reloadCategoryWith('mobile', _buildFilters(newF, uniqueOnly ? { unique_per_provider: 'true' } : {}))
            }}
          >
            <Text style={[styles.filterChipText, categoryFilters.mobile.world_data === true && styles.filterChipTextActive]}>World data</Text>
          </Pressable>
        </View>
      )}

      {activeCategory === 'internet' && (
        <View style={styles.categoryFilterRow}>
          {([undefined, 100, 300, 1000] as (number | undefined)[]).map(speed => {
            const active = (categoryFilters.internet.min_speed ?? undefined) === speed
            const label = speed === undefined ? 'Alle' : `${speed}+ Mbit`
            return (
              <Pressable
                key={String(speed)}
                style={[styles.filterChip, active && styles.filterChipActive]}
                onPress={() => {
                  haptics.selection()
                  const newF = { ...categoryFilters.internet, min_speed: speed }
                  setCategoryFilters(f => ({ ...f, internet: newF }))
                  reloadCategoryWith('internet', _buildFilters(newF, uniqueOnly ? { unique_per_provider: 'true' } : {}))
                }}
              >
                <Text style={[styles.filterChipText, active && styles.filterChipTextActive]}>{label}</Text>
              </Pressable>
            )
          })}
        </View>
      )}

      {activeCategory === 'energy' && (
        <View style={styles.categoryFilterRow}>
          <Pressable
            style={[styles.filterChip, categoryFilters.energy.green_energy === true && styles.filterChipActive]}
            onPress={() => {
              haptics.selection()
              const newF = { ...categoryFilters.energy, green_energy: categoryFilters.energy.green_energy === true ? undefined : true }
              setCategoryFilters(f => ({ ...f, energy: newF }))
              reloadCategoryWith('energy', _buildFilters(newF, uniqueOnly ? { unique_per_provider: 'true' } : {}))
            }}
          >
            <Text style={[styles.filterChipText, categoryFilters.energy.green_energy === true && styles.filterChipTextActive]}>Grøn strøm</Text>
          </Pressable>
          <Pressable
            style={[styles.filterChip, categoryFilters.energy.prepaid === true && styles.filterChipActive]}
            onPress={() => {
              haptics.selection()
              const newF = { ...categoryFilters.energy, prepaid: categoryFilters.energy.prepaid === true ? undefined : (true as boolean | undefined) }
              setCategoryFilters(f => ({ ...f, energy: newF }))
              reloadCategoryWith('energy', _buildFilters(newF, uniqueOnly ? { unique_per_provider: 'true' } : {}))
            }}
          >
            <Text style={[styles.filterChipText, categoryFilters.energy.prepaid === true && styles.filterChipTextActive]}>Forudbetalt</Text>
          </Pressable>
          <Pressable
            style={[styles.filterChip, categoryFilters.energy.prepaid === false && styles.filterChipActive]}
            onPress={() => {
              haptics.selection()
              const newF = { ...categoryFilters.energy, prepaid: categoryFilters.energy.prepaid === false ? undefined : (false as boolean | undefined) }
              setCategoryFilters(f => ({ ...f, energy: newF }))
              reloadCategoryWith('energy', _buildFilters(newF, uniqueOnly ? { unique_per_provider: 'true' } : {}))
            }}
          >
            <Text style={[styles.filterChipText, categoryFilters.energy.prepaid === false && styles.filterChipTextActive]}>Bagudbetalt</Text>
          </Pressable>
        </View>
      )}

      {/* Plans list */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : currentPlans.length === 0 ? (
        <View style={styles.emptyContainer}>
          <FileText size={40} color={colors.text.muted} />
          <Text style={styles.emptyText}>Ingen planer fundet</Text>
        </View>
      ) : (
        <View style={{ flex: 1 }}>
          <FlatList
            data={currentPlans}
            keyExtractor={(item) => item.id}
            contentContainerStyle={{ padding: spacing.md, paddingBottom: 100, gap: spacing.sm }}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={async () => {
                  setRefreshing(true)
                  const id = ++loadIdRef.current
                  await loadData(id)
                  setRefreshing(false)
                }}
                tintColor={colors.primary}
              />
            }
            renderItem={({ item: quote }) => {
              const annualSavings = getSavingsAnnual(quote)
              return (
                <SharedQuoteCard
                  quote={{ ...quote, status: 'received' } as QuoteData}
                  category={activeCategory}
                  savings={annualSavings}
                  tariffs={tariffs}
                  yearlyKwh={currentAgreement?.yearlyKwh}
                  smartBadge={badgeMap.get(quote.id) ?? null}
                  onAccept={() => setAcceptModalQuote({
                    ...quote,
                    estimated_annual_savings: annualSavings,
                  })}
                />
              )
            }}
          />
          {categoryLoading[activeCategory] && (
            <View style={styles.categoryLoadingOverlay}>
              <ActivityIndicator size="small" color={colors.primary} />
            </View>
          )}
        </View>
      )}

      </ContentContainer>
      {latchedAcceptQuote && (
        <AcceptQuoteModal
          visible={!!acceptModalQuote}
          quote={latchedAcceptQuote}
          onClose={() => setAcceptModalQuote(null)}
          userProfile={userProfile}
          tariffs={tariffs}
          yearlyKwh={currentAgreement?.yearlyKwh}
          hasPaymentInfo={hasPaymentInfo}
          existingPaymentReg={existingPaymentReg}
          existingPaymentAccount={existingPaymentAccount}
          onboardingPlans={modalOnboardingPlans}
          onAccept={async (data) => {
            if (!acceptModalQuote) return
            const quoteId = acceptModalQuote.id
            const providerName = acceptModalQuote.provider
            const isPublicPlan = quoteId.toString().startsWith('market-') || quoteId.toString().startsWith('public-')
            try {
              if (isPublicPlan) {
                const ca = currentAgreements[acceptModalQuote.category]
                const annualSavings = (ca && ca.priceConfirmed) ? Math.round((ca.price - acceptModalQuote.monthly_price) * 12) : undefined
                await apiClient.quotes.accept(quoteId, {
                  ...(data || {}),
                  public_plan: true,
                  category: acceptModalQuote.category,
                  provider: acceptModalQuote.provider,
                  product_name: acceptModalQuote.product_name,
                  monthly_price: acceptModalQuote.monthly_price,
                  estimated_annual_savings: annualSavings && annualSavings > 0 ? annualSavings : undefined,
                  details: acceptModalQuote.details,
                }, { _skipAuthRedirect: true } as any)
              } else {
                await apiClient.quotes.accept(quoteId, data)
              }
              appEvents.emitDebounced('inbox-updated')
              toast.success(`Vi kontakter ${providerName} — du hører fra os!`)
              setAcceptModalQuote(null)
            } catch (err: any) {
              sentryCaptureException(err, { action: 'accept_quote', screen: 'market' })
              toast.error('Kunne ikke acceptere tilbuddet — vi er notificeret. Prøv igen.')
            }
          }}
        />
      )}
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.surface },
  header: {
    flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    backgroundColor: colors.background, borderBottomWidth: 1, borderBottomColor: colors.border,
  },
  backButton: {
    width: 40, height: 40, alignItems: 'center', justifyContent: 'center',
    borderRadius: borderRadius.md, backgroundColor: colors.surface,
  },
  headerTitleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' },
  headerTitle: { ...typography.h2, color: colors.text.primary },
  headerSubtitle: { ...typography.caption, color: colors.text.secondary, marginTop: 2 },
  savingsBadge: {
    paddingHorizontal: 10, paddingVertical: 4, borderRadius: borderRadius.full,
    backgroundColor: colors.successMuted, borderWidth: 1, borderColor: colors.success,
  },
  savingsBadgeText: { fontSize: 11, fontWeight: '700', color: colors.successDark },
  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs, margin: spacing.md,
    padding: spacing.sm, backgroundColor: colors.warningLight, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.warningMuted,
  },
  errorText: { flex: 1, ...typography.small, color: colors.warningDark },
  errorRetry: { ...typography.captionBold, color: colors.warningDark },
  currentAgreement: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    marginHorizontal: spacing.md, marginTop: spacing.sm,
    padding: spacing.md, backgroundColor: colors.surface,
    borderRadius: borderRadius.lg, borderWidth: 1, borderColor: colors.border,
  },
  currentAgreementAvatar: {
    width: 36, height: 36, borderRadius: 18, backgroundColor: colors.border,
    alignItems: 'center', justifyContent: 'center',
  },
  currentAgreementInitial: { fontSize: 12, fontWeight: '700', color: colors.text.secondary },
  currentAgreementLabel: { ...typography.caption, color: colors.text.secondary },
  currentAgreementDetails: { fontSize: 10, color: colors.text.muted },
  currentAgreementPrice: { ...typography.bodyBold, color: colors.text.primary },
  currentAgreementUnit: { fontSize: 11, fontWeight: '400', color: colors.text.muted },
  categoryTabs: {
    flexDirection: 'row', gap: spacing.xs,
    paddingHorizontal: spacing.md, paddingTop: spacing.sm,
  },
  categoryTab: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    paddingVertical: 12, borderRadius: borderRadius.lg,
    backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border,
  },
  categoryTabActive: {
    backgroundColor: colors.background, borderColor: colors.border,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1,
  },
  categoryTabLabel: { ...typography.caption, color: colors.text.muted },
  categoryTabCount: { fontSize: 11, color: colors.text.muted },
  filterRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
  },
  filterChip: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 12, paddingVertical: 8,
    borderRadius: borderRadius.full, borderWidth: 1, borderColor: colors.border,
    backgroundColor: colors.background,
  },
  filterChipActive: {
    backgroundColor: colors.primary, borderColor: colors.primary,
  },
  filterChipText: { ...typography.caption, color: colors.text.secondary, fontWeight: '500' },
  filterChipTextActive: { color: colors.text.inverse },
  planCount: { ...typography.caption, color: colors.text.muted },
  categoryFilterRow: {
    flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs,
    paddingHorizontal: spacing.md, paddingBottom: spacing.sm,
  },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  emptyContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: spacing.sm },
  emptyText: { ...typography.body, color: colors.text.muted },
  categoryLoadingOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.7)', alignItems: 'center', justifyContent: 'center',
  },
})
